/**
 * Decides whether a demo-player visit counts as an admin view or a prospect
 * ("direct") view.
 *
 * Internal links carry ?source=admin. Once a browser has opened one, it is
 * remembered in localStorage (on the demo site's origin) as an admin's
 * browser, and every later visit from it counts as admin too -- even from a
 * link that lost or never had the parameter. The catch: anyone else using
 * that browser, e.g. a prospect watching on an admin's laptop, also counts
 * as admin.
 *
 * To forget a browser, open any demo link with ?adminViewer=off (easiest on a
 * phone), or delete the key in DevTools > Application > Local Storage, or run
 * localStorage.removeItem("jin_admin_viewer") in the console.
 */

export const ADMIN_VIEWER_KEY = "jin_admin_viewer";

// localStorage can throw (Safari with storage blocked, some embedded
// browsers). Fall back to the URL alone rather than breaking the player.
const storage = {
  get: () => {
    try {
      return window.localStorage.getItem(ADMIN_VIEWER_KEY);
    } catch (e) {
      return null;
    }
  },
  set: () => {
    try {
      window.localStorage.setItem(ADMIN_VIEWER_KEY, "1");
    } catch (e) {
      // not remembered; this visit still counts as admin via the URL
    }
  },
  remove: () => {
    try {
      window.localStorage.removeItem(ADMIN_VIEWER_KEY);
    } catch (e) {
      // nothing stored to forget
    }
  },
};

/** Returns "admin" or "direct" for the current visit. `search` is the
 *  router's location.search. */
export const getDemoViewSource = (search) => {
  const params = new URLSearchParams(search);

  // Forgetting is done by an admin, so this visit itself still counts as
  // admin; from the next visit on the browser is treated normally.
  if (params.get("adminViewer") === "off") {
    storage.remove();
    return "admin";
  }

  if (params.get("source") === "admin") {
    storage.set();
    return "admin";
  }

  return storage.get() === "1" ? "admin" : "direct";
};
